<script lang="ts">
  import { untrack } from 'svelte';
  import type { ShellController } from './shell/shell-controller.svelte';
  import type { SoundStore } from '../audio/sound-store';
  import type { MusicStore } from '../audio/music-store';
  import { socketState } from './socket-state.svelte';
  import { settingsOverlay } from './settings-open.svelte';
  import SettingsScreen from './SettingsScreen.svelte';
  import type { SettingsStore } from '../settings/settings-store';
  import PregameScreen from './shell/PregameScreen.svelte';
  import CornerPanel from './shell/CornerPanel.svelte';
  import LoginScreen from './shell/LoginScreen.svelte';
  import RegisterScreen from './shell/RegisterScreen.svelte';
  import RedeemScreen from './shell/RedeemScreen.svelte';
  import ShopScreen from './shell/ShopScreen.svelte';
  import LoadoutScreen from './shell/LoadoutScreen.svelte';
  import WorldPicker from './shell/WorldPicker.svelte';
  import LobbyBrowser from './shell/LobbyBrowser.svelte';
  import LobbyScreen from './shell/LobbyScreen.svelte';
  import IntroOverlay from './shell/IntroOverlay.svelte';
  import DebugMenu from './debug/DebugMenu.svelte';
  import { CLIENT_BUILD } from '../net/build-check';
  import { features } from '../features/feature-state.svelte';
  import { CUSTOM_LOBBIES, SHIP_CUSTOMIZATION } from '../features/overrides';

  interface Props {
    shell: ShellController;
    rememberedName: string;
    sounds: SoundStore;
    music: MusicStore;
    settings: SettingsStore;
  }

  const { shell, rememberedName, sounds, music, settings }: Props = $props();

  type IntroStage = 'title' | 'reveal' | 'panels' | 'done';
  let intro = $state<IntroStage>(untrack(() => settings.get('skipIntro')) ? 'done' : 'title');

  $effect(() => {
    shell.noteCreditBalance(socketState.credits);
  });

  let musicSelection = $state(untrack(() => music.selection));
  let musicCredit = $state(untrack(() => music.credit));

  function selectMusic(selection: number): void {
    music.selection = selection;
    musicSelection = music.selection;
    musicCredit = music.credit;
  }
</script>

<!-- Outside `.shell` so it survives the shell being hidden: the HUD opens this one too. -->
{#if settingsOverlay.open}
  <SettingsScreen
    {sounds}
    {settings}
    onclose={() => {
      settingsOverlay.hide();
    }}
  />
{/if}

<!-- Outside `.shell` for the same reason: a lobby seats you in a live room, so the shell is hidden
     while the roster is still being drawn. -->
{#if socketState.lobby !== null}
  <LobbyScreen
    lobby={socketState.lobby}
    selfId={socketState.selfId}
    ontake={(slotId) => {
      shell.takeLobbySeat(slotId);
    }}
    onspectate={() => {
      shell.spectateLobby();
    }}
    ondifficulty={(team, difficulty) => {
      shell.setTeamDifficulty(team, difficulty);
    }}
    chat={socketState.chat}
    onchat={(text) => {
      shell.sendChat(text);
    }}
    onstart={() => {
      shell.startLobby();
    }}
    oncancelstart={() => {
      shell.cancelLobbyStart();
    }}
    onleave={() => {
      shell.leaveLobby();
    }}
  />
{/if}

{#if features.debuggable}
  <DebugMenu
    oncommand={(type, amount) => {
      shell.sendDebugCommand(type, amount);
    }}
  />
{/if}

{#if intro !== 'done'}
  <IntroOverlay
    onreveal={() => (intro = 'reveal')}
    onpanels={() => (intro = 'panels')}
    ondone={() => (intro = 'done')}
  />
{/if}

<!-- The main plate stays mounted; every other screen lays a plate over it. The shop's plate is wide
     enough to reach the corner plate, so that one gives way rather than showing through it. -->
<div
  class="shell"
  class:shell--intro={intro === 'title'}
  class:shell--corner-out={intro === 'title' || intro === 'reveal'}
  hidden={!shell.visible}
>
  <PregameScreen {shell} {rememberedName} {sounds} {musicSelection} onMusicSelect={selectMusic} />
  {#if shell.screen !== 'shop'}
    <CornerPanel {shell} />
  {/if}

  {#if shell.screen !== 'pregame'}
    <div class="wis-scrim"></div>
  {/if}

  {#if shell.screen === 'login'}
    <LoginScreen {shell} />
  {:else if shell.screen === 'register'}
    <RegisterScreen {shell} />
  {:else if shell.screen === 'redeem'}
    <RedeemScreen {shell} />
  {:else if shell.screen === 'shop'}
    <ShopScreen {shell} />
  {:else if shell.screen === 'loadout' && features.enabled(SHIP_CUSTOMIZATION)}
    <LoadoutScreen
      initialShipId={shell.profile?.profileShip?.shipId ?? 0}
      team={0}
      loggedIn={shell.loggedIn}
      credits={shell.profile?.credit ?? 0}
      loadLoadout={(sid) => shell.loadLoadout(sid)}
      confirmBuild={(sid, build, expected) => shell.confirmLoadout(sid, build, expected)}
      onclose={() => {
        shell.navigate('pregame');
      }}
    />
  {:else if shell.screen === 'worlds'}
    <WorldPicker {shell} />
  {:else if shell.screen === 'lobbies' && features.enabled(CUSTOM_LOBBIES)}
    <LobbyBrowser
      rows={socketState.lobbyRows}
      message={shell.lobbyMessage}
      onjoin={(joinCode) => {
        shell.joinLobby(joinCode, true);
      }}
      oncreate={(visibility, name) => {
        shell.createLobby(visibility, name);
      }}
      onrefresh={() => {
        shell.refreshLobbies();
      }}
      onback={() => {
        shell.navigate('pregame');
      }}
    />
  {/if}

  <span class="shell__build" data-testid="shell-build">v{CLIENT_BUILD}</span>

  {#if musicCredit !== ''}
    <span class="shell__music-credit" data-testid="shell-music-credit">{musicCredit}</span>
  {/if}

  <!-- The HUD holds the other copy of this and stands down while the shell is up. -->
  {#if socketState.notification !== ''}
    <p class="shell__notification" role="alert" data-testid="shell-notification">
      {socketState.notification}
    </p>
  {/if}
</div>

<style>
  .shell {
    transition: opacity 300ms ease-out;
  }

  .shell :global(.wis-panel--corner) {
    transition:
      transform 500ms ease-out,
      opacity 300ms ease-out;
  }

  .shell--intro {
    opacity: 0;
    visibility: hidden;
  }

  .shell--corner-out :global(.wis-panel--corner) {
    transform: translateY(-160%);
    opacity: 0;
  }

  .shell__build {
    position: absolute;
    bottom: 4px;
    left: 6px;
    color: #8f8f8f;
    font-size: 11px;
    text-shadow: 0 1px 2px #000;
  }

  .shell__music-credit {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;
    color: #fff;
    font-size: 14px;
  }

  .shell__notification {
    position: absolute;
    top: 6px;
    left: 0;
    right: 0;
    margin: 0;
    z-index: 6;
    color: #ff3333;
    font-size: 15px;
    text-align: center;
    text-shadow: 0 1px 2px #000;
  }
</style>
